import React, { FC, useState, useMemo, useRef } from 'react';
import { useIntl } from 'umi';
import { Button, DatePicker } from 'tea-component';
import AutoSaveModal from '@sg/components/AutoSaveModal';
import CascadeProjectCustom from '@sg/components/CascadeProjectCustom';
import DatePickerWrapper from '@gp/components/DatePickerWrapper';
import { InDevProjectsTab, createBtnPoint, SGBuriedBtnPoint } from '@sg/utils/buriedPoint';
import InDevProjectsOverview from './component/InDevProjectsOverview';
import EditNotesModal from './component/EditNotesModal';
import { useInDevProjectsAll } from './hooks/useInDevProjectsAll';
import { useInDevProjectsAllAutoSave } from './hooks/useInDevProjectsAllAutoSave';
import type { ILiveVisualizationAllProps } from '@sg/pages/StudioManagement/InDevProjects/pages/InDevProjectsAll/type';
import Abnormal from '@home/components/Abnormal';
import SvgIcon from '@/pages/home/components/SvgIcon';
import { getCascadeProjectDatas, getCascadeProjectValues } from '@gp/utils/project';
import RoutePrompt from '@sg/components/RoutePrompt';
import { getButtonPermission } from '@sg/_config/permission';
import { usePermissionReady } from '@sg/hooks/usePermissionReady';
import './index.scss';
import { useDeepCompareEffect } from 'ahooks';
import GPSelectMultiple from '@gp/components/SelectMultiple';
import {
  STATUS_OPTIONS,
  MULTIPLE_SEPARATED,
  MILESTONE_OPTIONS,
} from '@sg/pages/StudioManagement/InDevProjects/pages/InDevProjectsAll/const';

/**
 * InDevProjectsAll — In Dev Projects 的聚合视图。
 * 当用户在容器 InDevProjects 中选择 "ALL" 时渲染。
 * 实现参照 LiveVisualizationAll，复用同一份 ILiveVisualizationAllProps 类型契约。
 */
const InDevProjectsAll: FC<ILiveVisualizationAllProps> = (props) => {
  const { studio, studioList } = props;
  const [isEditing, setIsEditing] = useState(false);
  // Edit Notes 弹窗状态（模拟数据，待接入真实数据源）
  const [isEditNotesVisible, setIsEditNotesVisible] = useState(false);
  const notesBeforeEditModalRef = useRef<any[] | null>(null);

  // Studio/Project 二级级联配置
  const cascadeInfoList = useMemo(
    () => [
      { level: 1, key: 'studio_id', nameKey: 'studio_name', title: 'Studio', isEndPanel: false },
      { level: 2, key: 'project_id', nameKey: 'game_title', title: 'Project', isEndPanel: true },
    ],
    [],
  );

  const { formatMessage } = useIntl();
  const { ready: permissionReady } = usePermissionReady();
  const { MonthPicker } = DatePicker;

  const editable = useMemo(
    () => getButtonPermission('studio_inDevProjects_inDevProjectsAll'),
    [permissionReady],
  );

  const {
    projects,
    projectListAll,
    notes,
    loading,
    handleDateFilterChange,
    disabledMonth,
    handleProjectsReorder,
    handleProjectsReorderCancel,
    handleConfirm,
    studioProjectList,
    isError,
    selectedMonth,
    setSelectedMonth,
    cascadeValue,
    setCascadeValue,
    changeFilterProjects,
    setNotes,
    setProjects,
    projectOrderList,
    setProjectOrderList,
    statusValue,
    setStatusValue,
    milestoneValue,
    setMilestoneValue,
  } = useInDevProjectsAll(studioList || []);

  const {
    autoSaveVisible,
    hasUnsavedChanges,
    captureBaseline,
    handleEditWithAutoSave,
    handleAutoSaveConfirm,
    handleAutoSaveCancel,
    clearAutoSave,
  } = useInDevProjectsAllAutoSave({
    isEditing,
    projects,
    notes,
    projectOrderList,
    setProjects,
    setNotes,
    setProjectOrderList,
  });

  const handleDateChange = (value: any) => {
    setSelectedMonth(value);
    handleDateFilterChange?.(value);
  };

  const handleEditNotesClose = () => {
    if (notesBeforeEditModalRef.current) {
      setNotes(notesBeforeEditModalRef.current);
    }
    notesBeforeEditModalRef.current = null;
    setIsEditNotesVisible(false);
  };

  const handleEditNotesConfirm = () => {
    notesBeforeEditModalRef.current = null;
    setIsEditNotesVisible(false);
  };

  // handleEditNotes 打开EditNotesModal
  const handleEditNotes = () => {
    notesBeforeEditModalRef.current = JSON.parse(JSON.stringify(notes || []));
    setIsEditNotesVisible(true);
  };

  const handleEditToggle = () => {
    if (isEditing) {
      setIsEditing(false);
      return;
    }
    handleEditWithAutoSave(() => {
      const btnPoint = createBtnPoint(InDevProjectsTab, 'inDevProjectsAllEdit', 'Edit InDevProjectsAll');
      if (btnPoint) {
        SGBuriedBtnPoint(btnPoint);
      }
      captureBaseline(projects || [], notes || [], projectOrderList || []);
      setIsEditing(true);
    });
  };

  const handleSave = async () => {
    await handleConfirm?.();
    clearAutoSave();
    setIsEditing(false);
  };

  const handleCancel = () => {
    handleProjectsReorderCancel?.();
    clearAutoSave();
    setIsEditing(false);
  };

  // 将 studioProjectList 转为级联树结构
  const cascadeTreeOptions = useMemo(() => {
    if (!studioProjectList || studioProjectList.length === 0) {
      return [];
    }
    // 转换 projects → child 以适配 getCascadeProjectDatas
    // studioProjectList 已由 getFormatProjectDatas 添加了 project_id
    // 将 studio_name 为空的项目合并到 "Others" 节点下，并将 Others 排在最末尾
    const OTHERS_LABEL = 'Others';
    const OTHERS_STUDIO_ID = '__others__';
    const namedStudios: any[] = [];
    const othersProjects: any[] = [];

    studioProjectList.forEach((studio: any) => {
      const studioName = studio.studio_name || studio.studio; // indev api use studio_name instead of studio
      const projectChildren = (studio.projects || []).map((p: any) => ({
        game_title: p.game_title,
        cover: p.cover,
        project_id: p.project_id,
      }));
      if (!studioName) {
        othersProjects.push(...projectChildren);
        return;
      }
      namedStudios.push({
        studio_id: studio.studio_id,
        studio_name: studioName,
        child: projectChildren,
      });
    });

    const transformed = [...namedStudios];
    if (othersProjects.length > 0) {
      transformed.push({
        studio_id: OTHERS_STUDIO_ID,
        studio_name: OTHERS_LABEL,
        child: othersProjects,
      });
    }
    return getCascadeProjectDatas(transformed, '', cascadeInfoList);
  }, [studioProjectList, cascadeInfoList]);

  // 计算所有最后节点的 project_id，修改getCascadeProjectValues方法
  const allCascadeValues = useMemo(() => {
    return getCascadeProjectValues(cascadeTreeOptions, false);
  }, [cascadeTreeOptions]);

  useDeepCompareEffect(() => {
    setCascadeValue(allCascadeValues);
  }, [allCascadeValues]);

  const handleCascadeChange = (values: string[]) => {
    changeFilterProjects(values);
    setCascadeValue(values);
  };
  const handleStatusChange = (values: string[]) => {
    const valueTemp = values?.length === STATUS_OPTIONS.length ? [] : values;
    changeFilterProjects(cascadeValue, valueTemp);
    setStatusValue(valueTemp);
  };
  const handleMilestoneChange = (values: string[]) => {
    const valueTemp = values?.length === MILESTONE_OPTIONS.length ? [] : values;
    changeFilterProjects(cascadeValue, statusValue, valueTemp);
    setMilestoneValue(valueTemp);
  };

  return (
    <div className="dbs-live-visualization-all dbs-home-studio-card-studio dbs-indev-projects-all">
      <div className="dbs-studio-filter">
        <div className="dbs-segment-row">
          <div className="dbs-segment-row__left">
            <DatePickerWrapper className="dbs-month-picker-with-tooltip" label={formatMessage({ id: 'dbs_month' })}>
              <MonthPicker
                overlayClassName="dbs-month-picker-overlay"
                value={selectedMonth}
                onChange={handleDateChange}
                disabledMonth={disabledMonth}
                placeholder="Please Select"
                disabled={isEditing}
              />
            </DatePickerWrapper>
            <CascadeProjectCustom
              options={cascadeTreeOptions}
              value={cascadeValue}
              onChange={handleCascadeChange}
              allValues={allCascadeValues}
              btnTitle="Studio/Project"
              disabled={isEditing}
              cascadeInfoList={cascadeInfoList}
            />
            <GPSelectMultiple
              groups={MULTIPLE_SEPARATED}
              label={'Status'}
              overlayClassName={'dbg-dynamic-filter-overlay dbg-dynamic-filter__select-overlay dbs-common-box'}
              options={STATUS_OPTIONS}
              onChange={handleStatusChange}
              value={statusValue}
              disabled={isEditing}
            />
            <GPSelectMultiple
              groups={MULTIPLE_SEPARATED}
              label={'Milestone'}
              overlayClassName={'dbg-dynamic-filter-overlay dbg-dynamic-filter__select-overlay dbs-common-box'}
              options={MILESTONE_OPTIONS}
              onChange={handleMilestoneChange}
              value={milestoneValue}
              disabled={isEditing}
            />
            {editable && isEditing && (
              <Button
                onClick={(e) => {
                  e?.stopPropagation(); // Prevent collapse event
                  handleEditNotes?.();
                }}
                style={{ height: '36px', minWidth: '80px' }}
                className="dbs-outline-btn"
                disabled={loading}
              >
                <SvgIcon type={'pencil'} className="prefix-icon" />
                {formatMessage({ id: 'dbs_edit_notes' })}
              </Button>
            )}
          </div>
          <div className="dbs-segment-row__right">
            {editable && !isEditing && (
              <Button
                onClick={(e) => {
                  e?.stopPropagation(); // Prevent collapse event
                  handleEditToggle();
                }}
                className="dbs-gradient-btn"
                disabled={loading}
              >
                {formatMessage({ id: 'dbs_edit' })}
              </Button>
            )}
            {editable && isEditing && (
              <div className="dbs-rich-text-buttons">
                <Button
                  onClick={(e) => {
                    e?.stopPropagation(); // Prevent collapse event
                    handleSave?.();
                  }}
                  style={{ marginRight: '10px' }}
                  className="dbs-gradient-btn"
                  disabled={loading}
                >
                  {formatMessage({ id: 'dbs_confirm' })}
                </Button>
                <Button
                  onClick={(e) => {
                    e?.stopPropagation(); // Prevent collapse event
                    handleCancel();
                  }}
                  style={{ height: '36px', minWidth: '80px' }}
                  className="dbs-outline-btn"
                  disabled={loading}
                >
                  {formatMessage({ id: 'dbs_cancel' })}
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      <Abnormal
        isLoading={loading}
        isError={isError}
        loadingMsg="Loading"
        component={
          <InDevProjectsOverview
            studio={studio}
            studioList={studioList}
            isEditing={isEditing}
            handleProjectsReorder={handleProjectsReorder}
            handleProjectsReorderCancel={handleProjectsReorderCancel}
            projects={projects}
            projectListAll={projectListAll}
            selectedMonth={selectedMonth}
          />
        }
      />

      {/* Edit Notes Modal */}
      <EditNotesModal
        visible={isEditNotesVisible}
        onClose={handleEditNotesClose}
        onConfirm={handleEditNotesConfirm}
        month={selectedMonth?.format?.('YYYY-MM') || ''}
        notes={notes}
        setNotes={setNotes}
      />

      <AutoSaveModal
        visible={autoSaveVisible}
        onConfirm={() => {
          handleAutoSaveConfirm();
          setIsEditing(true);
        }}
        onClose={() => {
          handleAutoSaveCancel();
          captureBaseline(projects || [], notes || [], projectOrderList || []);
          setIsEditing(true);
        }}
      />

      <RoutePrompt
        when={hasUnsavedChanges}
        onOk={async () => {
          await handleSave();
        }}
      />
    </div>
  );
};

export default InDevProjectsAll;
